import { createAsyncThunk } from "@reduxjs/toolkit";
import supportApi from "../../../../configs/apis/support";

const SEND_TIMEOUT_MS = 10000;

// Slice-action creators referenced as plain objects to avoid a circular
// import: support.slice.js already imports the createAsyncThunk thunks
// from this file. createSlice gives us deterministic action types
// `<sliceName>/<reducerName>` so we just dispatch those.
const SUPPORT = "support";
const ACT_ADD_PENDING_ADMIN_COMMENT = `${SUPPORT}/addPendingAdminComment`;
const ACT_MARK_ADMIN_COMMENT_SENT = `${SUPPORT}/markAdminCommentSent`;
const ACT_MARK_ADMIN_COMMENT_FAILED = `${SUPPORT}/markAdminCommentFailed`;

/**
 * Optimistic agent reply (WhatsApp-style send-status).
 *
 * Pushes a synthetic comment immediately, then races the addComment API
 * call against a 10-second timeout. The conversation flips clock -> tick
 * on success, or clock -> retry icon on failure / timeout.
 *
 * Returns { ok, comment, tempId, error } so the container can chain
 * attachment uploads only on a successful text send.
 *
 * Pass an explicit `tempId` to retry an existing failed bubble — the
 * slice replaces the entry in place rather than appending a new one.
 */
export const sendAdminComment =
  ({ ticketId, content, isInternal = false, tempId, userId, userName }) =>
  async (dispatch) => {
    const id =
      tempId || `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    dispatch({
      type: ACT_ADD_PENDING_ADMIN_COMMENT,
      payload: {
        ticketId,
        comment: {
          id,
          ticket_id: ticketId,
          user_id: userId || "self",
          user_name: userName || "You",
          user_type: "agent",
          content,
          is_internal: isInternal,
          created_at: new Date().toISOString(),
        },
      },
    });

    let timeoutHandle = null;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutHandle = setTimeout(
        () => reject(new Error("SEND_TIMEOUT")),
        SEND_TIMEOUT_MS
      );
    });

    try {
      const response = await Promise.race([
        supportApi.addComment({
          ticketId,
          content,
          isInternal,
        }),
        timeoutPromise,
      ]);
      if (timeoutHandle) clearTimeout(timeoutHandle);

      const serverComment = response?.data || response;
      dispatch({
        type: ACT_MARK_ADMIN_COMMENT_SENT,
        payload: { tempId: id, serverComment },
      });
      return { ok: true, comment: serverComment, tempId: id };
    } catch (err) {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      const isTimeout = err && err.message === "SEND_TIMEOUT";
      dispatch({
        type: ACT_MARK_ADMIN_COMMENT_FAILED,
        payload: {
          tempId: id,
          error: isTimeout ? "Timed out after 10s" : "Send failed",
        },
      });
      return { ok: false, tempId: id, error: err, isTimeout };
    }
  };

export const adminCreateTicket = createAsyncThunk(
  "support/adminCreateTicket",
  async (payload, { rejectWithValue }) =>
    supportApi
      .adminCreateTicket(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getTickets = createAsyncThunk(
  "support/getTickets",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getTickets(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getTicket = createAsyncThunk(
  "support/getTicket",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getTicket(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const assignTicket = createAsyncThunk(
  "support/assignTicket",
  async (payload, { rejectWithValue }) =>
    supportApi
      .assignTicket(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const updateTicketStatus = createAsyncThunk(
  "support/updateTicketStatus",
  async (payload, { rejectWithValue }) =>
    supportApi
      .updateTicketStatus(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const updateTicketPriority = createAsyncThunk(
  "support/updateTicketPriority",
  async (payload, { rejectWithValue }) =>
    supportApi
      .updateTicketPriority(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const addComment = createAsyncThunk(
  "support/addComment",
  async (payload, { rejectWithValue }) =>
    supportApi
      .addComment(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const uploadAttachment = createAsyncThunk(
  "support/uploadAttachment",
  async (payload, { rejectWithValue }) =>
    supportApi
      .uploadAttachment(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getAnalyticsOverview = createAsyncThunk(
  "support/getAnalyticsOverview",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getAnalyticsOverview(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getAnalyticsSla = createAsyncThunk(
  "support/getAnalyticsSla",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getAnalyticsSla(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getAgents = createAsyncThunk(
  "support/getAgents",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getAgents()
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getSlaConfigs = createAsyncThunk(
  "support/getSlaConfigs",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getSlaConfigs()
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const updateSlaConfig = createAsyncThunk(
  "support/updateSlaConfig",
  async (payload, { rejectWithValue }) =>
    supportApi
      .updateSlaConfig(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const acknowledgeTicket = createAsyncThunk(
  "support/acknowledgeTicket",
  async (payload, { rejectWithValue }) =>
    supportApi
      .acknowledgeTicket(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const escalateTicket = createAsyncThunk(
  "support/escalateTicket",
  async (payload, { rejectWithValue }) =>
    supportApi
      .escalateTicket(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getGlobalSlaConfigs = createAsyncThunk(
  "support/getGlobalSlaConfigs",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getGlobalSlaConfigs()
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const updateGlobalSlaConfig = createAsyncThunk(
  "support/updateGlobalSlaConfig",
  async (payload, { rejectWithValue }) =>
    supportApi
      .updateGlobalSlaConfig(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getBusinessHours = createAsyncThunk(
  "support/getBusinessHours",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getBusinessHours()
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const updateBusinessHours = createAsyncThunk(
  "support/updateBusinessHours",
  async (payload, { rejectWithValue }) =>
    supportApi
      .updateBusinessHours(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getHolidays = createAsyncThunk(
  "support/getHolidays",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getHolidays()
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const createHoliday = createAsyncThunk(
  "support/createHoliday",
  async (payload, { rejectWithValue }) =>
    supportApi
      .createHoliday(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const updateHoliday = createAsyncThunk(
  "support/updateHoliday",
  async (payload, { rejectWithValue }) =>
    supportApi
      .updateHoliday(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const deleteHoliday = createAsyncThunk(
  "support/deleteHoliday",
  async (payload, { rejectWithValue }) =>
    supportApi
      .deleteHoliday(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const getAutoReplies = createAsyncThunk(
  "support/getAutoReplies",
  async (payload, { rejectWithValue }) =>
    supportApi
      .getAutoReplies()
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);

export const updateAutoReply = createAsyncThunk(
  "support/updateAutoReply",
  async (payload, { rejectWithValue }) =>
    supportApi
      .updateAutoReply(payload)
      .then((response) => response)
      .catch((error) => rejectWithValue(error))
);
